import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { AtSign, Search as SearchIcon, Heart, Star, Smartphone, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * UsersTab — admin ops view of registered consumers.
 *
 * Compliance-aware: we surface identity + rollup activity totals, but we
 * deliberately DO NOT expose what individual users searched, favorited,
 * or reviewed. That's the same privacy line drawn everywhere else — the
 * ops team should never be able to say "@silver-leaf-42 likes Blue Dream
 * on Trulieve Reading" from this dashboard.
 */
export const UsersTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const refresh = (query = "") => {
    setLoading(true);
    api
      .get("/admin/users", { params: query ? { q: query } : {} })
      .then((r) => setRows(r.data?.results || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    refresh();
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    refresh(q.trim());
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="eyebrow mb-1">Accounts</div>
          <h2 className="font-serif text-2xl">Users on Bud Link</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-lg leading-relaxed">
            Identity + activity rollups only. Individual searches, favorites,
            or reviews are never surfaced here — that&apos;s intentional
            privacy for a medical-marijuana platform.
          </p>
        </div>
        <form onSubmit={submitSearch} className="flex gap-2 min-w-[240px]">
          <div className="relative flex-1">
            <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40 pointer-events-none" />
            <Input
              data-testid="users-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email / @handle"
              className="h-10 pl-9"
            />
          </div>
        </form>
      </div>

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 rounded-2xl" />
          <Skeleton className="h-16 rounded-2xl" />
          <Skeleton className="h-16 rounded-2xl" />
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-border rounded-2xl p-10 text-center">
          <div className="font-serif text-xl mb-2">No users match</div>
          <p className="text-sm text-muted-foreground">
            {q ? "Try a different search." : "Nobody's signed up yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground mb-2">
            {rows.length} account{rows.length === 1 ? "" : "s"}
          </div>
          {rows.map((u) => (
            <UserRow key={u.user_id} u={u} />
          ))}
        </div>
      )}
    </div>
  );
};

const UserRow = ({ u }) => {
  const created =
    u.created_at ? new Date(u.created_at).toLocaleDateString() : "—";
  const last =
    u.last_login_at ? new Date(u.last_login_at).toLocaleString() : null;

  return (
    <div
      data-testid={`user-row-${u.user_id}`}
      className="border border-border bg-card rounded-2xl p-4 flex items-center gap-4 flex-wrap"
    >
      <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center overflow-hidden shrink-0">
        {u.picture ? (
          <img src={u.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <AtSign className="w-4 h-4 text-foreground/40" />
        )}
      </div>
      <div className="flex-1 min-w-[180px]">
        <div className="flex items-center gap-2 flex-wrap">
          {u.username ? (
            <span className="font-medium">@{u.username}</span>
          ) : (
            <span className="text-foreground/50 italic text-sm">no nickname set</span>
          )}
          {u.display_name_hidden && (
            <span className="pill text-[10px] bg-secondary text-foreground/60 inline-flex items-center gap-1">
              <EyeOff className="w-3 h-3" /> real name hidden
            </span>
          )}
          {u.provider === "google" && (
            <span className="pill text-[10px] bg-sky-500/15 text-sky-300 border-sky-500/30">
              Google
            </span>
          )}
        </div>
        <div className="text-xs text-foreground/60 truncate">
          {u.email}
        </div>
        <div className="text-[11px] text-foreground/45 mt-0.5">
          Joined {created}
          {last ? ` · last active ${last}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs text-foreground/70 shrink-0">
        <span className="inline-flex items-center gap-1" title="Favorites">
          <Heart className="w-3.5 h-3.5" /> {u.favorite_count}
        </span>
        <span className="inline-flex items-center gap-1" title="Reviews">
          <Star className="w-3.5 h-3.5" /> {u.review_count}
        </span>
        <span className="inline-flex items-center gap-1" title="Push devices">
          <Smartphone className="w-3.5 h-3.5" /> {u.push_devices}
        </span>
      </div>
    </div>
  );
};

export default UsersTab;
